// Contact form — client-side validation + fetch submit

import { getStoredUtmParams } from "./lib/utm_tracking"

function bindContactForm(form) {
  if (!form || form.dataset.ctBound) return;
  form.dataset.ctBound = "true";

  const successPanel = form.parentElement.querySelector("#contact-success");

  function normalizePhone(value) {
    const rawValue = value?.trim() || "";
    if (!rawValue) return "";

    let normalized = rawValue.replace(/[^\d+]/g, "");
    if (normalized.startsWith("00")) normalized = `+${normalized.slice(2)}`;

    return normalized;
  }

  function isValidInternationalPhone(value) {
    return /^\+[1-9]\d{7,14}$/.test(normalizePhone(value));
  }

  // Per-field validation messages
  const MESSAGES = {
    name:      "Inserisci il tuo nome (minimo 2 caratteri).",
    surname:   "Inserisci il tuo cognome (minimo 2 caratteri).",
    email:     "Inserisci un indirizzo email valido (es. tu@azienda.it).",
    phone:     "Inserisci il numero in formato internazionale (es. +39 333 1234567).",
    company:   "Inserisci il nome della tua azienda.",
    marketing: "Questo consenso è obbligatorio per procedere.",
    privacy:   "Devi accettare la Privacy Policy per procedere.",
  };

  const fields = {
    name:    { el: form.querySelector("#ct-name"),     validate: v => v.trim().length >= 2 },
    surname: { el: form.querySelector("#ct-surname"),  validate: v => v.trim().length >= 2 },
    email:   { el: form.querySelector("#ct-email"),    validate: v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim()) },
    phone:   { el: form.querySelector("#ct-phone"),    validate: v => isValidInternationalPhone(v) },
    company: { el: form.querySelector("#ct-company"),  validate: v => v.trim().length >= 1 },
  };

  const roleEl = form.querySelector("#ct-role");

  const checkboxes = {
    marketing: form.querySelector("#ct-check-marketing"),
    privacy:   form.querySelector("#ct-check-privacy"),
  };

  const networkError = form.querySelector("#contact-error");
  const submitBtn    = form.querySelector(".ct-btn");

  // ── Error helpers ───────────────────────────────────────────────────────

  function setFieldError(key, msg) {
    const errEl = form.querySelector(`#ct-error-${key}`);
    if (!errEl) return;
    errEl.textContent = msg || "";
    errEl.hidden = !msg;
  }

  function setCheckboxError(key, msg) {
    setFieldError(key, msg);
    // highlight the custom checkbox box
    const checkEl = checkboxes[key];
    const box = checkEl?.nextElementSibling; // .ct-check-box
    box?.classList.toggle("ct-check-invalid", !!msg);
  }

  function clearAllErrors() {
    Object.keys(fields).forEach(k => {
      setFieldError(k, "");
      fields[k].el?.classList.remove("ct-invalid");
    });
    Object.keys(checkboxes).forEach(k => setCheckboxError(k, ""));
    if (networkError) { networkError.textContent = ""; networkError.hidden = true; }
  }

  function showNetworkError(msg) {
    if (networkError) { networkError.textContent = msg; networkError.hidden = false; }
  }

  // ── Live field events ───────────────────────────────────────────────────

  Object.entries(fields).forEach(([key, { el }]) => {
    if (!el) return;
    el.addEventListener("blur", () => {
      const ok = fields[key].validate(el.value);
      if (key === "phone" && ok) el.value = normalizePhone(el.value);
      el.classList.toggle("ct-invalid", !ok);
      setFieldError(key, ok ? "" : MESSAGES[key]);
    });
    el.addEventListener("input", () => {
      el.classList.remove("ct-invalid");
      setFieldError(key, "");
    });
  });

  Object.entries(checkboxes).forEach(([key, el]) => {
    el?.addEventListener("change", () => setCheckboxError(key, ""));
  });

  // ── Submit ───────────────────────────────────────────────────────────────

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    clearAllErrors();

    let firstInvalid = null;
    let formValid = true;

    // Text fields
    Object.entries(fields).forEach(([key, { el, validate }]) => {
      if (!el) return;
      const ok = validate(el.value);
      if (!ok) {
        el.classList.add("ct-invalid");
        setFieldError(key, MESSAGES[key]);
        if (!firstInvalid) firstInvalid = el;
        formValid = false;
      }
    });

    // Checkboxes — both required
    Object.entries(checkboxes).forEach(([key, el]) => {
      if (!el?.checked) {
        setCheckboxError(key, MESSAGES[key]);
        formValid = false;
      }
    });

    if (!formValid) {
      firstInvalid?.focus();
      return;
    }

    if (submitBtn) submitBtn.disabled = true;

    const csrf = () =>
      document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? "";

    const payload = {
      name:              fields.name.el?.value.trim(),
      surname:           fields.surname.el?.value.trim(),
      email:             fields.email.el?.value.trim(),
      phone:             normalizePhone(fields.phone.el?.value),
      company:           fields.company.el?.value.trim(),
      role:              roleEl?.value.trim() || "",
      message:           form.querySelector("#ct-message")?.value.trim(),
      consent_privacy:   String(checkboxes.privacy?.checked ?? false),
      consent_marketing: String(checkboxes.marketing?.checked ?? false),
      ...getStoredUtmParams(),
    };

    try {
      const res  = await fetch(form.dataset.url, {
        method:  "POST",
        headers: {
          "Content-Type":  "application/json",
          "Accept":        "application/json",
          "X-CSRF-Token":  csrf(),
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        showNetworkError(data.error || "Si è verificato un errore. Riprova più tardi.");
        return;
      }

      // Redirect if a success URL is configured, otherwise show inline panel
      const redirectUrl = data.redirect_url || form.dataset.successRedirect;
      if (redirectUrl) {
        const locationUrl = new URL(redirectUrl, window.location.origin);
        if (data.lead_ref && !data.redirect_url) {
          locationUrl.searchParams.set("lead_ref", data.lead_ref);
        }
        window.location.href = locationUrl.toString();
        return;
      }

      // Hide the form and reveal the success panel in its place
      form.hidden = true;
      if (successPanel) successPanel.hidden = false;
    } catch (_) {
      showNetworkError("Errore di rete. Controlla la connessione e riprova.");
    } finally {
      if (submitBtn) submitBtn.disabled = false;
    }
  });
}

function initContactForm() {
  document.querySelectorAll("form.contact-form").forEach(bindContactForm);
}

document.addEventListener("turbo:load", initContactForm);
if (document.readyState !== "loading") initContactForm();

